import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardSubtitle,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row
} from 'reactstrap';

import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';

import { getStateConversation } from 'utils/fuctions/getStateConversation';
import { useSocketConversationChatTs } from '../../../../../../../../../hooks/useSocketConversationChatTs';
import useAgentsLoggedInQueues from 'hooks/useAgentsLoggedInQueues';
import useQueues from 'hooks/useQueues';
import { zIndex } from 'constants/zIndex';

const sortOptionsByLabel = (options) => {
  if (!options?.length) return [];
  return [...options].sort((a, b) => {
    if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
    if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
    return 0;
  });
};

// Esse componente é responsável por transferir conversas para atendentes e/ou filas específicas no monitoramento de conversas,
// Especificamente, é o modal que fica disponível em cada linha da tabela.
// As regras de transferência estão seguindo o definido pelo "Projeto Melhorias Online"
// É possível ver o contexto do funcionamento no documento e na task a seguir:
// Escopo de Desenvolvimento: Padronização de Transferência e Extinção de Fila Sistêmica - https://docs.google.com/document/d/19lfSB-3kgpeqR9VJH0ZrtR_TF8k3OMHDvB8quS49eEg/edit?tab=t.0#heading=h.gk7u2aj0ihw
// Tarefa Clickup: https://app.clickup.com/t/86ae0qa4k
const TransferConversation = ({
  name,
  tooltipTitle = '',
  id,
  conversation,
  isCurrentAgentsLogged
}) => {
  const {
    allQueueOptions,
    allAgentOptions,
    queuesMap,
    agentsMap,
    fetch: fetchAgents
  } = useAgentsLoggedInQueues({ manual: true });

  const { response: allQueuesFromSystem, fetch: fetchQueues } = useQueues({
    showDisabled: false,
    types: ['is_chat'],
    manual: true
  });

  const { socketChatTS } = useSocketConversationChatTs();

  const { control, handleSubmit, errors, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      fila: null,
      user: null,
      transferToSelf: false
    }
  });

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const [isOpen, setIsOpen] = useState(false);
  const [transferMode, setTransferMode] = useState('QUEUE_FIRST');

  const [showOnlyWithAgents, setShowOnlyWithAgents] = useState(true);

  const conversationRef = useRef(conversation);
  const currentUserLogin = useRef(localStorage.getItem('login'));
  const currentUserName = useRef(localStorage.getItem('name'));
  const userState = useRef('DDD não definido');

  const watchedQueue = watch('fila');
  const watchedUser = watch('user');
  const watchedTransferToSelf = watch('transferToSelf');

  /**
   * Computed Options: Filas
   * Lógica de Filtragem Corrigida:
   * 1. Se showOnlyWithAgents=true: Filas apenas com agentes logados
   * 2. Se showOnlyWithAgents=false: TODAS as filas do sistema (com ou sem agentes)
   * 3. Aplica restrições adicionais por contexto (TransferToSelf, USER_FIRST)
   */
  const filteredQueueOptions = useMemo(() => {
    let options;

    if (showOnlyWithAgents) {
      options = allQueueOptions;
    } else {
      options = allQueuesFromSystem
        .filter((queue) => queue.is_chat && !queue.disable)
        .map((queue) => ({
          label: queue.name,
          value: queue.id
        }));
    }

    if (watchedTransferToSelf) {
      const myLogin = currentUserLogin.current;
      const myQueues = agentsMap[myLogin]?.queueOptions || [];
      options = options.filter((opt) =>
        myQueues.find((q) => q.value === opt.value)
      );
    } else if (transferMode === 'USER_FIRST' && watchedUser?.value) {
      const userQueues = agentsMap[watchedUser.value]?.queueOptions || [];
      options = options.filter((opt) =>
        userQueues.find((q) => q.value === opt.value)
      );
    }

    return sortOptionsByLabel(options);
  }, [
    transferMode,
    watchedUser,
    watchedTransferToSelf,
    agentsMap,
    allQueueOptions,
    showOnlyWithAgents,
    queuesMap,
    allQueuesFromSystem
  ]);

  /**
   * Computed Options: Agentes
   * Lógica de Filtragem:
   * 1. Se Mode == QUEUE_FIRST: Restringe aos agentes da fila selecionada.
   * 2. Exclui o próprio agente logado (atendente atual) da lista.
   */
  const filteredAgentOptions = useMemo(() => {
    let optionsToFilter = allAgentOptions;

    // Contexto: Fila selecionada define os agentes possíveis
    if (transferMode === 'QUEUE_FIRST' && watchedQueue?.value) {
      optionsToFilter = queuesMap[watchedQueue.value]?.agentOptions || [];
    }

    const currentAgentLogin = conversationRef.current?.atendente?.id_postgres;

    // Remove self da lista de transferência (exceto se for "Transferir para mim", tratado no checkbox separado)
    const validAgents = optionsToFilter.filter(
      (agent) => agent.value !== currentAgentLogin
    );

    return sortOptionsByLabel(validAgents);
  }, [transferMode, watchedQueue, queuesMap, allAgentOptions, agentsMap]);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
      fetchQueues();

      conversationRef.current = conversation;
      userState.current =
        (conversation?.cliente?.telefone &&
          getStateConversation(conversation.cliente?.telefone)) ||
        'DDD não definido';

      setTransferMode('QUEUE_FIRST');
      setShowOnlyWithAgents(true);
      setValue('fila', null);
      setValue('user', null);
      setValue('transferToSelf', false);
    }
  }, [isOpen, conversation, setValue, fetchAgents, fetchQueues]);

  // UX: Limpeza cruzada de campos ao mudar o modo de seleção
  useEffect(() => {
    if (transferMode === 'QUEUE_FIRST') setValue('user', null);
  }, [watchedQueue?.value, transferMode, setValue]);

  useEffect(() => {
    if (transferMode === 'USER_FIRST') setValue('fila', null);
  }, [watchedUser?.value, transferMode, setValue]);

  useEffect(() => {
    if (watchedTransferToSelf) setValue('user', null);
  }, [watchedTransferToSelf, setValue]);

  const handleTransfer = async (data) => {
    try {
      const targetUserLogin = data.transferToSelf
        ? currentUserLogin.current
        : data.user?.value;

      const filaId = data.fila?.value;
      let selectedAgentData = null;

      // Se houver um atendente alvo (seja self ou selecionado), resolvemos os dados do socket
      if (targetUserLogin) {
        if (data.transferToSelf) {
          selectedAgentData = {
            name: currentUserName.current,
            login: currentUserLogin.current
          };
        } else {
          const agentInfo = agentsMap[targetUserLogin];

          if (agentInfo) {
            selectedAgentData = {
              name: agentInfo.name,
              login: agentInfo.login
            };
          } else {
            const fallbackAgent = isCurrentAgentsLogged?.find(
              (a) => a.login === targetUserLogin
            );
            if (fallbackAgent) {
              selectedAgentData = {
                name: fallbackAgent.name,
                login: fallbackAgent.login
              };
            }
          }
        }

        if (!selectedAgentData) {
          throw new Error('Dados do agente não encontrados.');
        }
      }

      const payload = {
        chatId: id,
        socketAgent: socketChatTS?.id || null,
        destinationQueueId: filaId,
        destinationAgentLogin: targetUserLogin || undefined,
        isMultipleTransfer: false,
        responsibleForTransferred: {
          name: currentUserName.current,
          login: currentUserLogin.current
        }
      };

      socketChatTS.emit('transfer_chat', payload);

      setIsOpen(false);
    } catch (err) {
      console.error(err);
      const dataErr = err.response?.data;
      toast.error(dataErr?.message || 'Erro ao transferir conversa!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const toggleModal = () => setIsOpen(!isOpen);

  return (
    <>
      <Button
        size="sm"
        color="primary"
        title={tooltipTitle}
        onClick={toggleModal}
      >
        <i className="fas fa-random"></i>
      </Button>

      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={toggleModal}
        style={{ zIndex: zIndex.ChatDetailsModal }}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            {name}
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={toggleModal}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>

        <ModalBody>
          <Card style={{ backgroundColor: '#FFFFFF', marginTop: '-35px' }}>
            <CardBody>
              <CardTitle tag="h4">
                Cliente:{' '}
                {conversationRef.current?.cliente?.nome || 'Não informado'}
              </CardTitle>
              <CardSubtitle tag="h5" className="mb-3 text-muted">
                <Row>
                  <Col md="6" title="CPF do cliente">
                    <i
                      className="fas fa-id-card"
                      style={{ color: '#5bc0de' }}
                    ></i>
                    - {conversationRef.current?.cliente?.cpf || 'Não informado'}
                  </Col>
                  <Col md="6" title="Canal de atendimento">
                    <i
                      className="fas fa-users"
                      style={{ color: '#0275d8' }}
                    ></i>
                    - {conversationRef.current?.canal || 'Não reconhecido'}
                  </Col>
                </Row>
                <Row>
                  <Col md="6" title="Telefone do cliente">
                    <i
                      className="fas fa-phone"
                      style={{ color: '#5cb85c' }}
                    ></i>
                    -{' '}
                    {conversationRef.current?.cliente?.telefone ||
                      'Não informado'}
                  </Col>
                  <Col md="6" title="Estado">
                    <i className="fas fa-city" style={{ color: '#f0ad4e' }}></i>
                    {conversationRef.current?.cliente?.telefone &&
                      ` - Estado : ${userState.current}`}
                  </Col>
                </Row>
                <Row>
                  <Col md="6" title="Email do cliente">
                    <i
                      className="fas fa-envelope-open-text"
                      style={{ color: '#f0ad4e' }}
                    ></i>
                    -{' '}
                    {conversationRef.current?.cliente?.email || 'Não informado'}
                  </Col>
                  <Col md="6" title="Atendente">
                    <i className="fas fa-user" style={{ color: '#f0ad4e' }}></i>
                    {conversationRef.current?.atendente?.name
                      ? ` - ${conversationRef.current?.atendente?.name}`
                      : ' - Não informado'}
                  </Col>
                </Row>
              </CardSubtitle>
            </CardBody>
          </Card>

          <hr style={{ marginTop: '-20px' }} />

          <Form
            className="needs-validation"
            onSubmit={handleSubmit(handleTransfer)}
          >
            <Row className="mb-3">
              <Col md="12">
                <Label style={{ fontWeight: 'bold' }}>Modo de seleção:</Label>
                <div className="d-flex align-items-center">
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'QUEUE_FIRST'}
                        onChange={() => {
                          setTransferMode('QUEUE_FIRST');
                          setValue('user', null);
                        }}
                      />{' '}
                      Fila primeiro
                    </Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'USER_FIRST'}
                        onChange={() => {
                          setTransferMode('USER_FIRST');
                          setValue('fila', null);
                        }}
                      />{' '}
                      Atendente primeiro
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md="12">
                <FormGroup check>
                  <Label
                    check
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Controller
                      name="transferToSelf"
                      control={control}
                      defaultValue={false}
                      render={(props) => {
                        const { onChange, onBlur, value, ref } =
                          props.field || props;

                        return (
                          <Input
                            type="checkbox"
                            onChange={(e) => onChange(e.target.checked)}
                            onBlur={onBlur}
                            checked={value}
                            innerRef={ref}
                          />
                        );
                      }}
                    />{' '}
                    Transferir para mim mesmo
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            {transferMode === 'QUEUE_FIRST' && (
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup check>
                    <Label
                      check
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Oculta filas que não possuem nenhum agente logado no momento"
                    >
                      <Input
                        type="checkbox"
                        checked={showOnlyWithAgents}
                        onChange={(e) =>
                          setShowOnlyWithAgents(e.target.checked)
                        }
                      />{' '}
                      Mostrar apenas filas com atendentes logados
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md="12">
                {transferMode === 'QUEUE_FIRST' ? (
                  <>
                    {/* FLUXO QUEUE_FIRST: Fila é mandatória antes do Atendente */}
                    <div className="mb-3">
                      <FlexControllerV2
                        type="select"
                        name="fila"
                        control={control}
                        label="Fila"
                        options={filteredQueueOptions}
                        isClearable={false}
                        isSearchable={true}
                        rules={{ required: 'A fila é obrigatória' }}
                        errors={errors}
                        placeholder="Selecione a fila"
                      />
                    </div>

                    {watchedQueue && !watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* FLUXO USER_FIRST: Atendente é mandatório antes da Fila (exceto self) */}
                    {!watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}

                    {(watchedUser || watchedTransferToSelf) && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="fila"
                          control={control}
                          label="Fila"
                          options={filteredQueueOptions}
                          isClearable={false}
                          isSearchable={true}
                          rules={{ required: 'A fila é obrigatória' }}
                          errors={errors}
                          placeholder="Selecione a fila"
                        />
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button
                color="danger"
                data-dismiss="modal"
                type="button"
                onClick={toggleModal}
                style={{
                  ...buttonStyle(systemConfiguration.cancelation_button_color),
                  marginRight: '10px'
                }}
              >
                Fechar
              </Button>

              <Button
                color="primary"
                type="submit"
                style={buttonStyle(
                  systemConfiguration.confirmation_button_color
                )}
              >
                {watchedTransferToSelf ? 'Transferir para mim' : 'Transferir'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TransferConversation;
